/**
 * One conversation in the rail: its state dot, pin, topic, module tag and row menu. Every rail section draws its rows
 * with it (pinned, groups, time sections, search results).
 *
 * Dot semantics (comp `.dot`, `.dot.live`, `.dot.attn`):
 *   success — an agent turn is running right now
 *   warning — the conversation is blocked on the user
 *   neutral — idle
 */
import type React from 'react';
import { Pin } from 'lucide-react';
import { Button } from '../../components/ui/Button.js';
import { StatusDot } from '../../components/ui/StatusDot.js';
import type { Conversation } from '../../generated/api/types.gen.js';
import { ConversationRowMenu } from './ConversationRowMenu.js';
import { railRowClass } from './rail-row.js';
import { moduleTag } from './simpleModuleCopy.js';

interface Props {
  conversation: Conversation;
  active: boolean;
  live: boolean;
  showTag: boolean;
  onSelect: (conversationId: string) => void;
}

export const ConversationRailRow: React.FC<Props> = ({ conversation, active, live, showTag, onSelect }) => {
  const id = String(conversation.id);
  const variant = live ? 'success' : conversation.needsUser ? 'warning' : 'neutral';
  const pinned = conversation.pinnedAt != null;
  return (
    // `group` drives the row menu's hover reveal; `has-[[aria-expanded=true]]` keeps the row lit while its menu is
    // open. The row is a div wrapping a button because the menu's trigger is a button too and nesting one inside
    // another is invalid.
    <div
      className={`group relative flex items-center gap-2 rounded-[7px] pr-1 has-[[aria-expanded=true]]:bg-surface ${
        active ? 'bg-accent-muted' : 'hover:bg-surface'
      }`}
    >
      <Button
        variant="ghost"
        onClick={() => onSelect(id)}
        aria-current={active ? 'true' : undefined}
        className={railRowClass({
          active,
          className: `flex-1 bg-transparent hover:bg-transparent ${active ? '' : 'group-hover:text-body'}`,
        })}
      >
        <StatusDot variant={variant} size="sm" />
        {pinned && <Pin size={11} className="flex-none text-faint" aria-label="Pinned" />}
        <span className="overflow-hidden text-ellipsis">{conversation.topic}</span>
        {showTag && (
          <span className="ml-auto font-mono text-[9.5px] tracking-[0.5px] text-faint">
            {moduleTag(conversation.moduleId)}
          </span>
        )}
      </Button>
      <ConversationRowMenu conversationId={id} topic={conversation.topic} pinned={pinned} />
    </div>
  );
};
